// Birikim Dağılımı — Termometre'nin altındaki breakdown kartı.
// Task 16: SADECE LİKİT (fonlar + tekne gibi termometer-flag'li other_assets).
// Properties (Işık) burada DEĞİL — DiğerVarliklar kartında "ayrı tutulan" listesinde.
// Satılınca otomatik likit'e dönüşür (fon nakdine akar).
//
// Yeni satır: "Hedefe nakit ihtiyacı" — `cash_needed_for_goal_tl` prominent gösterimi.

import { fmtTLCompact } from "../lib/format";
import type { PortfolioCombined } from "../types/api";

interface Props {
  combined: PortfolioCombined | null;
}

function pctOf(part: number, total: number | null | undefined): number {
  if (total === null || total === undefined || total <= 0) return 0;
  if (!Number.isFinite(part) || !Number.isFinite(total)) return 0;
  return Math.max(0, Math.min(100, (part / total) * 100));
}

function safeNum(n: number | null | undefined): number {
  return n === null || n === undefined || !Number.isFinite(n) ? 0 : n;
}

const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

/** "2028-05" → "Mayıs 2028"; geçersizse olduğu gibi döner. */
function fmtPeriodTR(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const mi = Number(m[2]) - 1;
  if (mi < 0 || mi > 11) return period;
  return `${TR_MONTHS[mi]} ${m[1]}`;
}

export default function BirikimDagilimi({ combined }: Props) {
  if (!combined) {
    return (
      <section className="px-4 mt-4" aria-label="Likit birikim">
        <h3 className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-1 mb-2">
          Likit Birikim
        </h3>
        <div className="rounded-xl bg-ink-800 border border-ink-700 p-4 text-ink-400 text-xs">
          Veri yok — Ayarlar'dan token gir.
        </div>
      </section>
    );
  }

  // Task 16: SADECE LİKİT
  //   total = fonlar + tekne gibi termometer-flag'li other_assets.
  //   Properties (Işık) ayrı kartta (DigerVarliklar).
  const fonlar = safeNum(combined.combined?.total_value_tl);
  const total = safeNum(
    combined.combined?.total_with_termometer_assets_tl ??
      // Eski backend fallback'i — total_with_termometer'a düşmezse en azından fonlar.
      combined.combined?.total_value_tl,
  );
  const cashNeeded = safeNum(combined.combined?.cash_needed_for_goal_tl);
  const evHedef = combined.hedef_tl ?? null;
  const reserveTl = safeNum(combined.combined?.liquidity_reserve_tl);
  const effectiveGoal = safeNum(combined.combined?.effective_goal_tl);
  const liqAtRisk = combined.combined?.liquidity_at_risk ?? false;
  const liqStale = combined.liquidity?.is_stale ?? false;
  // Task 16 — include_in_termometer=true asset'ler (tekne gibi). Properties + BES bu listeye girmez.
  const termometerAssets = (combined.other_assets?.items ?? []).filter(
    (a) => a.include_in_termometer,
  );

  // Yüzdeler "hedef toplam" (ev + tampon) üzerinden — Termometre ile simetrik.
  const denom = effectiveGoal > 0 ? effectiveGoal : null;
  const fonlarPct = pctOf(fonlar, denom);
  const toplamPct = pctOf(total, denom);
  const cashNeededPct = pctOf(cashNeeded, denom);
  // Task 17 — Otomatik aylık tasarruf hedefi
  const savings = combined.combined?.savings ?? null;

  return (
    <section className="px-4 mt-4" aria-label="Birikim dağılımı">
      <h3 className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-1 mb-2">
        Likit Birikim
      </h3>
      <div className="rounded-xl bg-ink-800 border border-ink-700 p-3 space-y-3">
        <Row
          label="Fonlar"
          subLabel="Gökhan + anne + eş"
          value={fonlar}
          pct={fonlarPct}
          color="bg-accent-500"
        />

        {/* Task 16 — Termometre dahil diğer likit varlıklar (tekne vb.; BES + Işık değil) */}
        {termometerAssets.map((a) => (
          <Row
            key={a.id}
            label={a.name}
            subLabel={
              a.kind === "vehicle"
                ? "manuel TL"
                : a.applied_source === "tufe"
                  ? "TÜFE büyüme"
                  : "anchor"
            }
            value={safeNum(a.estimated_value_tl)}
            pct={pctOf(safeNum(a.estimated_value_tl), denom)}
            color="bg-ink-400"
            stale={a.is_stale}
          />
        ))}

        <div className="border-t border-ink-700 pt-3">
          {/* "Toplam likit" satırı yalnızca birden fazla likit kalem varsa anlamlı
              (Fonlar + tekne gibi). Tek kalem (sadece fonlar) ise Fonlar satırı
              zaten toplamı veriyor → tekrar göstermeyiz. */}
          {termometerAssets.length > 0 && (
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-ink-200 text-sm font-medium">Toplam likit</span>
              <span className="fon-dili text-ink-100 text-sm font-semibold">
                {fmtTLCompact(total)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-ink-400">Hedef toplam</span>
            <span className="fon-dili text-ink-300 text-xs">
              {fmtTLCompact(effectiveGoal)}
              <span className="text-ink-500 ml-1">
                · %{Math.round(toplamPct)} dolu
              </span>
            </span>
          </div>
          {/* Task 14 — hedef breakdown (ev + tampon) */}
          {effectiveGoal > 0 && (
            <div className="flex items-center justify-between mt-0.5">
              <span className="text-[10px] text-ink-500">
                <span title="Hareketli ev hedefi — max(KFE, TÜFE) ile büyür">
                  ev {fmtTLCompact(evHedef)}
                </span>
                <span className="text-ink-600 mx-1">+</span>
                <span
                  title="Likidite yastığı — eve girince elde kalması gereken minimum (Gökhan + eş ikisi de işsiz senaryosu)"
                  className={liqStale ? "text-warn-400" : undefined}
                >
                  yastık {fmtTLCompact(reserveTl)}
                  {liqStale && (
                    <span className="ml-0.5 text-[9px] uppercase tracking-wider">
                      gec
                    </span>
                  )}
                </span>
              </span>
              <span className="text-[10px] text-ink-500" />
            </div>
          )}
        </div>

        {/* Task 16 — Hedefe nakit ihtiyacı (prominent satır) */}
        {cashNeeded > 0 && (
          <div className="border-t border-ink-700 pt-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-ink-100 text-sm font-medium">
                Hedefe nakit ihtiyacı
              </span>
              <span className="fon-dili text-warn-400 text-base font-semibold">
                {fmtTLCompact(cashNeeded)}
              </span>
            </div>
            <p
              className="text-[10px] text-ink-500 leading-snug"
              title="Hedef toplam − bugünkü likit. Henüz satılmamış asset'ler (Işık vb.) burada yok — likidite olunca otomatik düşülür."
            >
              Hedef toplam ile likit birikim arası fark — eve girip yastığı
              koruyabilmek için bu kadar nakit daha gerekli.
              {effectiveGoal > 0 && (
                <span className="text-ink-600 ml-1">
                  · hedefin %{Math.round(cashNeededPct)}'i
                </span>
              )}
            </p>
          </div>
        )}

        {/* Task 17 — Tahmini varış (ANA) + sabit tarih → gereken (İKİNCİL) */}
        {savings && (
          <div className="border-t border-ink-700 pt-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-ink-100 text-sm font-medium">
                Tahmini varış
              </span>
              {savings.reachable && savings.arrival_period ? (
                <span className="fon-dili text-accent-400 text-base font-semibold">
                  {fmtPeriodTR(savings.arrival_period)}
                </span>
              ) : (
                <span className="fon-dili text-warn-400 text-sm font-semibold">
                  bu tempoyle ulaşılmıyor
                </span>
              )}
            </div>
            <p
              className="text-[10px] text-ink-500 leading-snug"
              title="Mevcut aylık tasarruf temposuyla hedefe (ev + yastık) tahmini varış. Taban = likit + Işık (satılıp eve gireceği için sayılır). Reel (bugünün parası); beklenen getiri muhafazakar 0. Gelir verisi gelince tempo son 3 ayın gerçekleşen ortalamasına geçer → tarih ay-ay dinamik oynar."
            >
              {savings.monthly_savings_source === "assumed"
                ? "Varsayılan"
                : "Son 3 ay"}{" "}
              {fmtTLCompact(savings.monthly_savings_tl)}/ay tempoyle
              {savings.months_to_goal != null &&
                ` · ~${savings.months_to_goal} ay`}
              . Işık satışı hedefe sayıldı; kalan açık{" "}
              {fmtTLCompact(savings.gap_tl)}.
            </p>
            {savings.required_monthly_tl > 0 && (
              <p className="text-[10px] text-ink-600 leading-snug mt-1">
                {fmtPeriodTR(savings.target_period)}'de istersen aylık{" "}
                <span className="text-ink-400 font-medium">
                  {fmtTLCompact(savings.required_monthly_tl)}
                </span>{" "}
                gerek.
              </p>
            )}
          </div>
        )}

        {/* Likidite riski bayrağı — bugünkü likit tamponun altına düştüyse */}
        {liqAtRisk && (
          <div className="rounded-lg bg-loss-500/10 border border-loss-500/40 px-2 py-1.5">
            <p className="text-[10px] text-loss-400 font-semibold uppercase tracking-wider">
              Likidite riski
            </p>
            <p className="text-[11px] text-ink-200 mt-0.5 leading-snug">
              Likit birikim ({fmtTLCompact(total)}) yastığın (
              {fmtTLCompact(reserveTl)}) altında — bugün eve girilirse nakit
              yastığı kalmaz.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

interface RowProps {
  label: string;
  subLabel?: string;
  value: number;
  pct: number;
  color: string;
  stale?: boolean;
}

function Row({ label, subLabel, value, pct, color, stale }: RowProps) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-ink-100 text-sm font-medium truncate">
            {label}
          </span>
          {subLabel ? (
            <span className="text-[10px] text-ink-400 truncate">{subLabel}</span>
          ) : null}
          {stale ? (
            <span
              className="text-[9px] uppercase tracking-wider text-warn-400 font-semibold"
              title="Endeks gecikmeli"
            >
              gec
            </span>
          ) : null}
        </div>
        <span className="fon-dili text-ink-200 text-sm whitespace-nowrap pl-2">
          {fmtTLCompact(value)}
        </span>
      </div>
      <div className="relative h-1.5 rounded-full bg-ink-700 overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${color} transition-[width] duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
